<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import { getMaterialLink } from '~/shared/lib/getMaterialLink';
import type { Swiper as SwiperClass } from 'swiper';
import { Navigation } from 'swiper/modules';
import type { NavigationOptions } from 'swiper/types';
import 'swiper/css';
import type { EventRelatedNewsDTO } from '~/shared/api/client/events/events.dto';
import MaterialCardImage from './MaterialCardImage.vue';
import MaterialCardText from './MaterialCardText.vue';

const props = defineProps<{
  relatedNews: EventRelatedNewsDTO[];
}>();

const { $viewport } = useNuxtApp();

const isMobile = computed(() => $viewport.isLessOrEquals('mobile'));

const isControlVisible = computed(() => {
  const count = props.relatedNews.length;
  let visibleSlides: number;

  switch ($viewport.breakpoint.value) {
    case 'desktop':
    case 'tabletBig':
      visibleSlides = 3;
      break;
    case 'tablet':
    case 'mobileBig':
      visibleSlides = 2;
      break;
    default:
      visibleSlides = 1;
      break;
  }

  return count > visibleSlides;
});

const swiperRef = ref<{ swiper: SwiperClass } | null>(null);
const navigationOptions = ref<NavigationOptions>({
  prevEl: null,
  nextEl: null,
});
const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);

onMounted(() => {
  if (prevEl.value && nextEl.value) {
    navigationOptions.value = {
      prevEl: prevEl.value,
      nextEl: nextEl.value,
    };
  }

  watch(
    () => isControlVisible.value,
    (newValue) => {
      const swiper = swiperRef.value?.swiper;
      if (!swiper) return;

      if (!newValue) {
        swiper.navigation?.destroy();
      } else {
        nextTick(() => {
          if (prevEl.value && nextEl.value && swiper.navigation) {
            swiper.navigation.destroy();
            swiper.navigation.init();
            swiper.navigation.update();
          }
        });
      }
    },
  );
});
</script>

<template>
  <div v-if="relatedNews.length" :class="$style.section">
    <div :class="$style.header">
      <h3 :class="$style.title">Материалы по мероприятию</h3>
      <div v-show="isControlVisible" :class="$style.controls">
        <div ref="prevEl" :class="$style.controlBtn">
          <Icon :class="$style.controlIcon" name="my-icon:i-arrow-left" />
        </div>
        <div ref="nextEl" :class="$style.controlBtn">
          <Icon :class="$style.controlIcon" name="my-icon:i-arrow-right" />
        </div>
      </div>
    </div>

    <Swiper
      ref="swiperRef"
      :observer="true"
      :observe-parents="true"
      :class="$style.swiperContainer"
      :modules="[Navigation]"
      :grab-cursor="true"
      :loop="relatedNews.length > 3"
      :navigation="navigationOptions"
      :slides-per-view="1"
      :slides-per-group="1"
      :breakpoints="{
        600: { slidesPerView: 2 },
        1024: { slidesPerView: 3 },
      }"
    >
      <SwiperSlide
        v-for="item in relatedNews"
        :key="item.id"
        :class="$style.slide"
      >
        <MaterialCardImage
          v-if="item.image?.photo?.file_url && !isMobile"
          :image="item.image.photo.file_url"
          :title="item.title"
          :link="getMaterialLink(item)"
        />
        <MaterialCardText
          v-else
          :title="item.title"
          :link="getMaterialLink(item)"
        />
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<style module lang="scss">
.section {
  background-color: var(--color-gray-bg);
  overflow: hidden;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding-left: var(--space-2-5x);
}

.title {
  @include text_h5;
  font-size: 24px;
  color: var(--color-black-1);
  margin: 0;
}

.controls {
  display: flex;
  align-self: stretch;

  path {
    fill: var(--color-black-1);
  }
}

.controlBtn {
  display: flex;
  width: 100px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background-color: var(--color-gray-bg);
  border-left: 1px solid var(--color-gray-line);

  &:hover {
    background-color: var(--color-gray-line);
  }
}

.controlIcon {
  width: 20px;
  height: 20px;
}

.swiperContainer {
  // 2px wider than the section so the last slide's border-right
  // gets clipped by overflow: hidden (2px covers subpixel rounding)
  width: calc(100% + 2px);
  border-top: 1px solid var(--color-gray-line);
}

.slide {
  height: auto;
  box-sizing: border-box;
  border-right: 1px solid var(--color-gray-line);
}

@media (max-width: 1379px) {
  .section {
    margin-left: 20px;
    margin-right: 20px;
  }
}

@media (max-width: 767px) {
  .title {
    font-size: 18px;
  }
}

@media (max-width: 599px) {
  .header {
    padding-left: var(--space-2x);
  }

  .title {
    font-size: 16px;
  }

  .header {
    height: 46px;
  }

  .controlBtn {
    width: 72px;
  }

  .slide {
    min-width: 240px;
  }
}
</style>
